import request from '../utils/request'
import { encrypt } from '../utils/rsa'

// RSA公钥缓存（避免每次登录都重新获取）
let cachedPublicKey = null
let publicKeyCacheTime = 0
let cachedBaseUrl = null // 记录缓存公钥时的baseUrl
const PUBLIC_KEY_CACHE_DURATION = 30 * 60 * 1000 // 缓存30分钟
let publicKeyFetchingPromise = null // 正在获取公钥的Promise，避免并发请求

/**
 * 清除公钥缓存（当baseUrl变化时调用）
 */
export function clearPublicKeyCache() {
  cachedPublicKey = null
  publicKeyCacheTime = 0
  cachedBaseUrl = null
  publicKeyFetchingPromise = null
  console.log('已清除RSA公钥缓存')
}

/**
 * 获取RSA公钥（带缓存机制，避免重复请求）
 * @param {Boolean} forceRefresh 是否强制刷新，忽略缓存
 */
export function getRsaPublicKey(forceRefresh = false) {
  // 获取当前baseUrl（从request工具中获取，或使用默认值）
  const baseUrl = getBaseUrl()
  
  // 检查缓存
  const now = Date.now()
  if (!forceRefresh && cachedPublicKey && cachedBaseUrl === baseUrl && (now - publicKeyCacheTime) < PUBLIC_KEY_CACHE_DURATION) {
    console.log('使用缓存的RSA公钥')
    return Promise.resolve({
      code: '000000',
      data: {
        publicKey: cachedPublicKey
      }
    })
  }
  
  // 如果baseUrl变化了，清除旧缓存
  if (cachedBaseUrl && cachedBaseUrl !== baseUrl) {
    console.log('baseUrl已变化，清除旧公钥缓存')
    clearPublicKeyCache()
  }
  
  // 如果正在获取公钥，直接返回正在进行的Promise，避免并发请求
  if (publicKeyFetchingPromise) {
    console.log('公钥正在获取中，等待现有请求完成')
    return publicKeyFetchingPromise
  }
  
  // 创建获取公钥的Promise
  publicKeyFetchingPromise = request({
    url: '/auth/rsa-public-key',
    method: 'get'
  }).then(response => {
    if (response.code === '000000' && response.data && response.data.publicKey) {
      console.log('RSA公钥获取成功')
      // 缓存公钥和baseUrl
      cachedPublicKey = response.data.publicKey
      publicKeyCacheTime = Date.now()
      cachedBaseUrl = baseUrl
      publicKeyFetchingPromise = null // 清除正在获取的标记
      return response
    } else {
      publicKeyFetchingPromise = null // 清除正在获取的标记
      throw new Error(response.msg || response.message || '获取公钥失败')
    }
  }).catch(error => {
    publicKeyFetchingPromise = null // 清除正在获取的标记
    throw error
  })
  
  return publicKeyFetchingPromise
}

/**
 * 获取当前baseUrl（从环境变量或当前页面中获取）
 */
function getBaseUrl() {
  // 从环境变量或默认值获取baseURL
  try {
    const baseURL = process.env.VUE_APP_BASE_API || 'http://localhost:8086'
    // 提取协议和主机部分（去掉路径）
    const url = new URL(baseURL)
    return `${url.protocol}//${url.host}`
  } catch (e) {
    // 如果解析失败，尝试从window.location获取
    try {
      if (typeof window !== 'undefined') {
        return window.location.origin
      }
    } catch (e2) {
      console.warn('无法获取baseUrl，使用默认值')
    }
    return ''
  }
}

/**
 * 生成随机数（用于防止重放攻击）
 */
function generateNonce() {
  return Math.random().toString(36).substring(2, 15) + 
         Math.random().toString(36).substring(2, 15) + 
         Date.now().toString(36)
}

/**
 * 登录
 * @param {Object} data 登录数据
 * @param {String} data.username 用户名/工号
 * @param {String} data.password 密码（明文，会自动加密）
 * @param {String} data.userType 用户类型（可选，如果不传则根据用户名密码自动判断）
 */
export async function login(data) {
  try {
    // 生成时间戳和随机数（防止重放攻击）
    const timestamp = Date.now()
    const nonce = generateNonce()
    
    // 获取RSA公钥（带缓存，避免重复请求）
    // 如果获取失败，清除缓存并重试一次
    let publicKeyResponse = await getRsaPublicKey()
    if (publicKeyResponse.code !== '000000' || !publicKeyResponse.data || !publicKeyResponse.data.publicKey) {
      // 清除缓存并重试一次
      console.warn('首次获取公钥失败，清除缓存后重试')
      clearPublicKeyCache()
      publicKeyResponse = await getRsaPublicKey()
      if (publicKeyResponse.code !== '000000' || !publicKeyResponse.data || !publicKeyResponse.data.publicKey) {
        throw new Error('获取加密密钥失败，请重试')
      }
    }
    
    const publicKey = publicKeyResponse.data.publicKey
    if (!publicKey || typeof publicKey !== 'string' || publicKey.trim().length === 0) {
      throw new Error('公钥格式无效，请重试')
    }
    
    // 加密密码（如果加密失败，清除缓存并重试一次）
    let encryptedPassword
    try {
      encryptedPassword = await encrypt(data.password, publicKey)
      if (!encryptedPassword || typeof encryptedPassword !== 'string' || encryptedPassword.trim().length === 0) {
        throw new Error('密码加密结果无效')
      }
    } catch (encryptError) {
      console.warn('首次加密失败，清除缓存后重试:', encryptError)
      // 清除缓存并强制重新获取公钥
      clearPublicKeyCache()
      const retryPublicKeyResponse = await getRsaPublicKey(true) // 强制刷新
      if (retryPublicKeyResponse.code !== '000000' || !retryPublicKeyResponse.data || !retryPublicKeyResponse.data.publicKey) {
        throw new Error('重新获取加密密钥失败，请重试')
      }
      const retryPublicKey = retryPublicKeyResponse.data.publicKey
      if (!retryPublicKey || typeof retryPublicKey !== 'string' || retryPublicKey.trim().length === 0) {
        throw new Error('重新获取的公钥格式无效，请重试')
      }
      try {
        encryptedPassword = await encrypt(data.password, retryPublicKey)
        if (!encryptedPassword || typeof encryptedPassword !== 'string' || encryptedPassword.trim().length === 0) {
          throw new Error('密码加密结果无效')
        }
      } catch (retryError) {
        console.error('重试加密仍然失败:', retryError)
        throw new Error('密码加密失败，请检查网络连接或联系管理员: ' + (retryError.message || '未知错误'))
      }
    }
    
    // 发送登录请求（密码已加密，包含时间戳和随机数）
    try {
      // 构建请求数据，不传递 userType（如果为空或不存在）
      const requestData = {
        username: data.username,
        password: encryptedPassword,
        encrypted: true,
        timestamp: timestamp,
        nonce: nonce
      }
      
      // 只有当 userType 存在且不为空时才传递
      if (data.userType && typeof data.userType === 'string' && data.userType.trim() !== '') {
        requestData.userType = data.userType.trim()
      }
      
      console.log('登录请求数据:', {
        username: requestData.username,
        hasPassword: !!requestData.password,
        encrypted: requestData.encrypted,
        timestamp: requestData.timestamp,
        nonce: requestData.nonce,
        userType: requestData.userType || '(未传递)'
      })
      
      return await request({
        url: '/auth/login',
        method: 'post',
        data: requestData
      })
    } catch (loginError) {
      // 如果登录失败且错误信息包含"密码格式错误"或"密码解密失败"，清除公钥缓存并重试一次
      const errorMessage = loginError.message || loginError.msg || ''
      if (errorMessage.includes('密码格式错误') || 
          errorMessage.includes('密码解密失败') || 
          errorMessage.includes('请刷新页面')) {
        console.warn('登录失败，可能是公钥不匹配，清除缓存后重试:', errorMessage)
        clearPublicKeyCache()
        
        // 重新获取公钥并加密
        const newPublicKeyResponse = await getRsaPublicKey(true) // 强制刷新
        if (newPublicKeyResponse.code !== '000000' || !newPublicKeyResponse.data || !newPublicKeyResponse.data.publicKey) {
          throw new Error('重新获取加密密钥失败，请重试')
        }
        const newPublicKey = newPublicKeyResponse.data.publicKey
        const newEncryptedPassword = await encrypt(data.password, newPublicKey)
        
        // 生成新的时间戳和随机数
        const newTimestamp = Date.now()
        const newNonce = generateNonce()
        
        // 重试登录
        return await request({
          url: '/auth/login',
          method: 'post',
          data: {
            ...data,
            password: newEncryptedPassword,
            encrypted: true,
            timestamp: newTimestamp,
            nonce: newNonce
          }
        })
      }
      // 其他错误直接抛出
      throw loginError
    }
  } catch (error) {
    console.error('登录失败:', error)
    throw error
  }
}

/**
 * 刷新令牌
 * @param {String} refreshToken 刷新令牌
 */
export function refreshToken(refreshToken) {
  // 使用 URLSearchParams 确保参数以表单格式发送，以便后端 @RequestParam 能正确接收
  const params = new URLSearchParams()
  params.append('refreshToken', refreshToken)
  return request({
    url: '/auth/refresh',
    method: 'post',
    data: params,
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded'
    }
  })
}

/**
 * 登出
 */
export function logout() {
  return request({
    url: '/auth/logout',
    method: 'post'
  })
}

/**
 * 修改密码
 * @param {Object} data { oldPassword, newPassword, confirmPassword }
 */
export function changePassword(data) {
  return request({
    url: '/auth/change-password',
    method: 'post',
    data
  })
}

/**
 * 验证当前密码是否正确
 * @param {string} password
 */
export function verifyPassword(password) {
  return request({
    url: '/auth/verify-password',
    method: 'post',
    data: { password }
  })
}

/**
 * 忘记密码 - 根据用户名或手机号重置为默认密码 000000
 * @param {string} identifier 用户名或会员手机号
 */
export function forgotPassword(identifier) {
  return request({
    url: '/auth/forgot-password',
    method: 'post',
    params: { identifier }
  })
}

